import type { RefObject } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import isMobile from 'Vendor/isMobile';

interface NavCallbacks {
  keyboardHandler: (e: KeyboardEvent) => void;
  resetNav: () => void;
}

export default function useDropdownNav(
  rootRef: RefObject<Element>,
  setVisibility: (visible: boolean) => void,
): NavCallbacks {
  const itemsRef = useRef<(HTMLElement | null)[]>([]);
  const [index, setIndex] = useState(-2);

  useEffect(function setRefs() {
    const root = rootRef.current;
    if (root) {
      itemsRef.current = Array.from(root.querySelectorAll<HTMLElement>('.nav-item'));
      itemsRef.current[-1] = root.querySelector<HTMLElement>('.nav-root');
    }
  });
  useEffect(
    function addEventListeners() {
      const root = rootRef.current;
      if (root) {
        const onFocusIn = (event: Event) => {
          const { target } = event;
          setIndex(target instanceof HTMLElement ? [...itemsRef.current].indexOf(target) : -2);
        };
        root.addEventListener('focusin', onFocusIn);
        return () => root.removeEventListener('focusin', onFocusIn);
      }
    },
    [rootRef],
  );
  useEffect(
    function indexAndFocusSync() {
      const elem = itemsRef.current[index];
      if (elem) {
        elem.focus();
      }
    },
    [index],
  );

  const getLastIndex = useCallback(() => itemsRef.current.length - 1, []);

  const next = useCallback((i: number) => {
    const nextIndex = i + 1;
    return nextIndex < itemsRef.current.length ? nextIndex : -1;
  }, []);

  const previous = useCallback(
    (i: number) => {
      const prevIndex = i - 1;
      return prevIndex > -2 ? prevIndex : getLastIndex();
    },
    [getLastIndex],
  );

  const keyboardHandler = useCallback(
    (e: KeyboardEvent) => {
      if (!rootRef.current) {
        return;
      }
      const { key, target } = e;
      const indexGetter =
        (
          {
            ArrowDown: next,
            ArrowUp: previous,
            End: homeEndHandler(target, getLastIndex),
            Escape: () => -1,
            Home: homeEndHandler(target, () => -1),
          } as Partial<Record<KeyboardEvent['key'], (index: number) => number | null>>
        )[key] || (() => null);

      const focusIndex = indexGetter(index);
      if (focusIndex !== null) {
        setVisibility(true);
        setIndex(focusIndex);
        // prevents scrolling
        e.preventDefault();
      }
    },
    [index, getLastIndex, next, previous, rootRef, setVisibility],
  );

  const resetNav = useCallback(() => {
    if (isMobile) {
      const focused = itemsRef.current[index];
      if (focused) {
        focused.blur();
      }
    } else {
      setIndex(-1);
    }
  }, [index]);

  return { keyboardHandler, resetNav };
}

const homeEndIgnoreInputTypes = ['text', 'search'];
function homeEndHandler(target: EventTarget | null, getter: () => number) {
  return target instanceof HTMLInputElement && homeEndIgnoreInputTypes.includes(target.type)
    ? () => null
    : getter;
}
